/**
 * ImageSlider Component
 * Handles image navigation and display for the Visions Special layout
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { DashedLabel } from './HelperComponents';
import PaintingPreview from '../paintingpreview';

interface ArtImage {
  mainSrc: string;
  alt: string;
  width: number;
  height: number;
  title: string;
  dimensions: string;
  aspectRatio: number;
  images: {
    main: string;
    normal: string;
    depth: string;
    roughness: string;
  };
}

interface ImageSliderProps {
  artImages: ArtImage[] | undefined;
  isFullscreen: boolean;
  onFullscreenToggle: () => void;
  showZoomHint: boolean;
  onOpenFullscreen: (src: string) => void;
}

export const ImageSlider: React.FC<ImageSliderProps> = ({
  artImages,
  isFullscreen,
  onFullscreenToggle,
  showZoomHint,
  onOpenFullscreen: _onOpenFullscreen
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handlePrevImage = useCallback(() => {
    if (artImages && artImages.length > 0) {
      setCurrentImageIndex(prev => (prev === 0 ? artImages.length - 1 : prev - 1));
    }
  }, [artImages]);

  const handleNextImage = useCallback(() => {
    if (artImages && artImages.length > 0) {
      setCurrentImageIndex(prev => (prev === artImages.length - 1 ? 0 : prev + 1));
    }
  }, [artImages]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextImage();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNextImage, handlePrevImage]);

  if (!artImages || artImages.length === 0) {
    return null;
  }

  const currentImage = artImages[currentImageIndex];

  return (
    <div className="relative">
      {/* Image Info Labels */}
      <div id="chapter-image-info" className="flex flex-col justify-center items-center uppercase gap-2 mb-4">
        <DashedLabel
          label="artwork"
          value={currentImage.title}
          id="chapter-image-info-title"
        />
        <DashedLabel
          label="dimensions"
          value={currentImage.dimensions}
          id="chapter-image-info-dimensions"
        />
      </div>

      {/* Main Image Display */}
      <div className={`relative ${isFullscreen ? 'fullscreen-mode' : ''}`}>
        {/* 3D Preview if available */}
        {currentImage.images.normal && currentImage.images.depth && artImages && (
          <PaintingPreview
            artImages={artImages}
            currentImageIndex={currentImageIndex}
            isFullscreen={isFullscreen}
            showZoomHint={showZoomHint}
          />
        )}

        {/* Navigation Buttons */}
        {artImages.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Fullscreen Button */}
        <button
          onClick={onFullscreenToggle}
          className="absolute top-2 right-2 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
          aria-label="Toggle fullscreen"
        >
          <Maximize2 className="w-5 h-5" />
        </button>

        {/* Image Counter */}
        {artImages.length > 1 && (
          <div className="absolute bottom-2 left-2 z-10 bg-black/50 text-white px-3 py-1 rounded text-[10px] uppercase">
            {currentImageIndex + 1} / {artImages.length}
          </div>
        )}

        {/* Zoom Hint */}
        {showZoomHint && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 bg-black/70 text-white px-4 py-2 rounded animate-fade-out">
            <p className="text-[14px]">Use scroll to zoom</p>
          </div>
        )}
      </div>
    </div>
  );
};